import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { CatalogView } from '../components/CatalogView';
import { useAuth } from '../context/AuthContext';
import {
  Car,
  Bike,
  Ship,
  Caravan,
  Truck,
  Building2,
  Plus,
  Shield,
  FileCheck,
  AlertCircle,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const SUBCAT_BY_SLUG = {
  cars: 'cars',
  motorbikes: 'motorbikes',
  caravans: 'caravans',
  boats: 'boats',
  trucks: 'trucks',
  commercial: 'commercial',
};

const HERO_BY_SLUG = {
  cars: {
    icon: Car,
    title: 'WANTD Cars',
    tagline: 'Private classifieds — PPSR bundled on Plus listings. Classifieds-only, no payment routing.',
  },
  motorbikes: {
    icon: Bike,
    title: 'WANTD Motorbikes',
    tagline: 'Road, dirt, track, project. Private sellers, local buyers, no middleman.',
  },
  caravans: {
    icon: Caravan,
    title: 'WANTD Caravans & RVs',
    tagline: 'Tourers, poptops, campers. Gas certs and weight disclosures encouraged.',
  },
  boats: {
    icon: Ship,
    title: 'WANTD Boats',
    tagline: 'Tinnies to trailer boats. Local delivery or on-water handover.',
  },
  trucks: {
    icon: Truck,
    title: 'WANTD Trucks',
    tagline: 'Utes, light commercial, heavy haulage. Coming soon.',
    comingSoon: true,
  },
  commercial: {
    icon: Building2,
    title: 'WANTD Commercial',
    tagline: 'Vans, buses, service vehicles. Coming soon.',
    comingSoon: true,
  },
};

// Secondary pill row — For Sale / Wrecks & Parts / Motorsport
const VEHICLE_TYPE_PILLS = [
  { id: 'for_sale', label: 'For Sale' },
  { id: 'wrecks_parts', label: 'Wrecks & Parts' },
  { id: 'motorsport', label: 'Motorsport' },
];

function AutoHero({ slug, user, onListClick }) {
  const config = HERO_BY_SLUG[slug] || HERO_BY_SLUG.cars;
  const Icon = config.icon;
  return (
    <div className="px-4 md:px-6 pt-4">
      <div
        className="rounded-2xl p-6"
        style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)' }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Icon className="w-6 h-6 text-[#F8F5F0]" />
              <h1 className="text-xl font-bold text-[#F8F5F0]">{config.title}</h1>
            </div>
            <p className="text-sm max-w-md" style={{ color: 'rgba(248,245,240,0.8)' }}>
              {config.tagline}
            </p>
            <div className="flex flex-wrap gap-3 mt-3 text-xs" style={{ color: 'rgba(248,245,240,0.6)' }}>
              <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Quality-scored listings</span>
              <span className="flex items-center gap-1"><FileCheck className="w-3.5 h-3.5" /> Plus $20 · PPSR bundled</span>
            </div>
          </div>
          {config.comingSoon ? (
            <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-[rgba(248,245,240,0.15)] text-[#F8F5F0] border border-[rgba(248,245,240,0.3)]">
              Coming Soon
            </span>
          ) : user && (
            <Button
              onClick={onListClick}
              className="bg-[#F8F5F0] text-[#1E3A5F] hover:bg-white font-bold flex items-center gap-2 flex-shrink-0 rounded-xl"
            >
              <Plus className="w-4 h-4" />
              List a Vehicle
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function AutoTermsBanner({ onDismiss }) {
  return (
    <div className="mx-3 md:mx-4 mt-3 flex items-start gap-3 p-4 rounded-lg border border-[var(--theme-border)] bg-[var(--theme-card)]">
      <AlertCircle className="w-5 h-5 text-[#D97706] mt-0.5 flex-shrink-0" />
      <p className="text-sm text-[var(--theme-text-secondary)] flex-1">
        WANTD Auto is a classifieds service — WANTD is not a party to any vehicle transaction. PPSR checks are informational only. By using this section you agree to our{' '}
        <Link to="/auto/terms" className="underline font-medium text-[var(--theme-text-primary)]">Auto Terms & Conditions</Link>.
      </p>
      <button
        onClick={onDismiss}
        className="text-[var(--theme-text-muted)] hover:text-[var(--theme-text-primary)] flex-shrink-0"
        aria-label="Dismiss terms banner"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function AutoLandingPage({ slug = 'cars' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const config = HERO_BY_SLUG[slug] || HERO_BY_SLUG.cars;
  const subcategory = SUBCAT_BY_SLUG[slug] || 'cars';

  const [termsBannerDismissed, setTermsBannerDismissed] = useState(
    () => localStorage.getItem('auto_terms_seen') === 'true'
  );
  const dismissTermsBanner = () => {
    localStorage.setItem('auto_terms_seen', 'true');
    setTermsBannerDismissed(true);
  };

  // Secondary filter: For Sale vs Wrecks & Parts vs Motorsport
  const [vehicleType, setVehicleType] = useState('for_sale');

  const extraParams = useMemo(() => {
    if (vehicleType === 'wrecks_parts') return { is_wrecks_parts: true };
    if (vehicleType === 'motorsport') return { is_motorsport: true };
    // for_sale: explicitly filter out wrecks/motorsport so they don't bleed into the default view
    return { is_wrecks_parts: false, is_motorsport: false };
  }, [vehicleType]);

  // Coming soon — just show the hero, no catalog
  if (config.comingSoon) {
    return (
      <Layout>
        <div className={`w-full ${user ? '-mt-4 md:-mt-6' : '-mt-5 md:-mt-8'}`}>
          <AutoHero slug={slug} user={user} onListClick={() => navigate(`/auto/${slug}/list`)} />
          <div className="px-4 md:px-6 mt-8 flex flex-col items-center text-center gap-3">
            <p className="text-[var(--theme-text-secondary)] text-sm max-w-sm">
              We're working on bringing {config.title} to WANTD. Check back soon.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  // Compose banner: terms banner + secondary pill row both live in bannerSlot
  const banner = (
    <>
      {!termsBannerDismissed && <AutoTermsBanner onDismiss={dismissTermsBanner} />}
      <div className="mx-3 md:mx-4 mt-3 flex gap-2 overflow-x-auto hide-scrollbar">
        {VEHICLE_TYPE_PILLS.map((p) => (
          <button
            key={p.id}
            onClick={() => setVehicleType(p.id)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
              vehicleType === p.id
                ? 'bg-[#1E3A5F] text-[#F8F5F0] border-[#1E3A5F]'
                : 'bg-[var(--theme-card)] text-[var(--theme-text-secondary)] border-[var(--theme-border)] hover:border-[#1E3A5F] hover:text-[var(--theme-text-primary)]'
            }`}
            data-testid={`vehicle-type-pill-${p.id}`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </>
  );

  return (
    <Layout>
      <div className={`w-full ${user ? '-mt-4 md:-mt-6' : '-mt-5 md:-mt-8'}`}>
        <CatalogView
          lockedCategory="vehicles"
          lockedSubcategory={subcategory}
          linkPrefix={`/auto/${slug}`}
          extraParams={extraParams}
          heroSlot={<AutoHero slug={slug} user={user} onListClick={() => navigate(`/auto/${slug}/list`)} />}
          bannerSlot={banner}
          showCategoryDropdown={false}
          showViewToggle={false}
          sectionTitle={config.title}
          itemNoun="vehicles"
          defaultSortBy="quality_recency"
        />
      </div>
    </Layout>
  );
}
